import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Button, Container, Navbar, Offcanvas} from "react-bootstrap";
import {FaBars, FaBell, FaBoxOpen, FaChevronRight} from "react-icons/fa";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {VendorContext} from "../../context/VendorContext";
import "../../assets/Css/Homedashboard.css";

const ACTIONABLE_STATUSES = new Set(["PENDING", "ADMIN_REVIEW", "ASSIGNED_TO_VENDOR"]);

const Header = ({title, toggleSidebar}) => {
    const user = useSelector((state) => state.userData);
    const {getvendorDetail, vendorProfile, orders, AllOrders} = useContext(VendorContext);
    const [showAlerts, setShowAlerts] = useState(false);
    const navigate = useNavigate();
    const contextActions = useRef({getvendorDetail, AllOrders});
    const orderLoadAttempted = useRef(false);
    contextActions.current = {getvendorDetail, AllOrders};

    useEffect(() => {
        if (user?.id) contextActions.current.getvendorDetail(user.id);
    }, [user?.id]);

    useEffect(() => {
        if (!orderLoadAttempted.current && !orders?.length) {
            orderLoadAttempted.current = true;
            contextActions.current.AllOrders(1);
        }
    }, [orders?.length]); // Load order-backed alerts on pages opened directly.

    const orderAlerts = useMemo(() => (orders || []).flatMap((order) =>
        (order?.order_items || [])
            .filter((item) => ACTIONABLE_STATUSES.has(item?.order_status))
            .map((item) => ({order, item}))
    ).slice(0, 8), [orders]);

    const displayName = vendorProfile?.store_name || user?.full_name || "Vendor account";
    const profileImage = vendorProfile?.store_logo
        ? `https://api.motospar.com${vendorProfile.store_logo}`
        : require("../../assets/images/profileIcon.png");

    const openAlert = (alert) => {
        setShowAlerts(false);
        navigate("/orderDetail", {state: {orderDetail: alert.order, itemdetail: alert.item}});
    };

    return (
        <>
            <Navbar className="custom-header">
                <Container fluid className="header-inner">
                    <div className="d-flex align-items-center gap-3">
                        <Button
                            variant="light"
                            className="header-menu-button d-md-none"
                            onClick={toggleSidebar}
                            aria-label="Open navigation menu"
                        >
                            <FaBars />
                        </Button>
                        <div>
                            <div className="header-eyebrow">MOTOSPAR VENDOR PORTAL</div>
                            <h1 className="header-title">{title}</h1>
                        </div>
                    </div>

                    <div className="header-actions">
                        <Button
                            variant="light"
                            className="notification-button"
                            onClick={() => setShowAlerts(true)}
                            aria-label={`Open order alerts${orderAlerts.length ? `, ${orderAlerts.length} pending` : ""}`}
                        >
                            <FaBell />
                            {orderAlerts.length > 0 && <span className="notification-count">{orderAlerts.length}</span>}
                        </Button>
                        <button className="header-profile" onClick={() => navigate("/Profile")} type="button">
                            <img
                                src={profileImage}
                                alt=""
                                className="profile-image"
                                onError={(event) => { event.currentTarget.src = require("../../assets/images/profileIcon.png"); }}
                            />
                            <span className="header-profile-copy">
                                <strong>{displayName}</strong>
                                <small>Manage store profile</small>
                            </span>
                            <FaChevronRight className="profile-chevron" />
                        </button>
                    </div>
                </Container>
            </Navbar>

            <Offcanvas show={showAlerts} onHide={() => setShowAlerts(false)} placement="end" className="alerts-panel">
                <Offcanvas.Header closeButton>
                    <div>
                        <div className="header-eyebrow">STAY ON TOP OF YOUR STORE</div>
                        <Offcanvas.Title>Order alerts</Offcanvas.Title>
                    </div>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {orderAlerts.length ? (
                        <>
                            <p className="alerts-intro">Orders waiting for your attention.</p>
                            <div className="alerts-list">
                                {orderAlerts.map(({order, item}, index) => (
                                    <button className="alert-item" key={`${item?.id || order?.id}-${index}`} onClick={() => openAlert({order, item})}>
                                        <span className="alert-icon"><FaBoxOpen /></span>
                                        <span className="alert-copy">
                                            <strong>{item?.product?.name || "New order item"}</strong>
                                            <small>Order {order?.order_code || "—"} · {String(item?.order_status || "").replaceAll("_", " ")}</small>
                                        </span>
                                        <FaChevronRight className="alert-chevron" />
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="alerts-empty">
                            <span className="alerts-empty-icon"><FaBell /></span>
                            <h3>You’re all caught up</h3>
                            <p>New orders that need attention will appear here.</p>
                        </div>
                    )}
                    <Button className="alerts-view-all" onClick={() => { setShowAlerts(false); navigate("/allOrders"); }}>
                        View all orders <FaChevronRight />
                    </Button>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default Header;
